import React, { useState, useRef, useEffect, useCallback } from "react";
import { HiSearch, HiX } from "react-icons/hi";
import { GiCoins } from "react-icons/gi";
import { MdSend } from "react-icons/md";
import axios from "axios";
import Swal from "sweetalert2";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const inputBase =
  "w-full py-2 sm:py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-800 outline-none transition focus:border-[#c51369] focus:ring-2 focus:ring-[#c51369]/10 focus:bg-white placeholder:text-gray-400";
const inputPlain = `${inputBase} px-3 sm:px-4`;
const inputWithIcon = `${inputBase} pl-9 pr-3`;
const inputWithRupee = `${inputBase} pl-9 pr-3`;

function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const SendMoneyModal = ({ isOpen, onClose, onTransaction }) => {
  const [phone, setPhone] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [mobileStep, setMobileStep] = useState(0);

  const debouncedPhone = useDebounce(phone, 400);
  const abortRef = useRef(null);

  // ── Fetch staff list ───────────────────────────────────────────────────────
  const fetchStaff = useCallback(async (search = "") => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingStaff(true);
    setFetchError("");

    try {
      const params = { page: 1, limit: 20 };
      if (search.trim()) params.search = search.trim();

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/staff/get-all-staff`,
        { params, signal: controller.signal }
      );

      setStaffList(res.data?.data || []);
    } catch (err) {
      if (axios.isCancel(err) || err.name === "CanceledError") return;
      setFetchError(
        err.response?.data?.message || err.message || "Unable to fetch staff."
      );
      setStaffList([]);
    } finally {
      setLoadingStaff(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchStaff(debouncedPhone);

    setSelectedStaff((prev) => {
      if (!prev || !debouncedPhone.trim()) return prev;
      const matches =
        prev.phoneNumber?.includes(debouncedPhone.trim()) ||
        prev.fullName
          ?.toLowerCase()
          .includes(debouncedPhone.trim().toLowerCase());
      return matches ? prev : null;
    });
  }, [isOpen, debouncedPhone, fetchStaff]);

  useEffect(() => {
    if (isOpen) setMobileStep(0);
  }, [isOpen]);

  const handlePhoneChange = (val) => {
    setPhone(val.replace(/\D/g, "").slice(0, 10));
  };

  const handleSelectStaff = (staff) => {
    setSelectedStaff(staff);
    setMobileStep(1);
  };

  // ── Send coins API ─────────────────────────────────────────────────────────
  const handleSend = async () => {
    const parsed = parseFloat(amount);
    if (!selectedStaff || !parsed || parsed <= 0) return;

    // Confirm before sending
    const confirm = await Swal.fire({
      title: "Confirm Transfer",
      html: `
        <div style="font-size:14px;color:#374151;line-height:1.7">
          Send <strong style="color:#c51369"> ${parsed.toLocaleString(
            "en-IN"
          )} Coins </strong>
          to <strong>${selectedStaff.fullName}</strong>?
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#c51369",
      cancelButtonColor: "#e5e7eb",
      customClass: {
        cancelButton: "!text-gray-700",
        popup: "!rounded-2xl",
        confirmButton: "!rounded-xl",
        cancelButton: "!rounded-xl",
      },
    });

    if (!confirm.isConfirmed) return;

    setSending(true);

    try {
      const token = localStorage.getItem("HeartConnect-Token");

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/staff-wallet/add-coins`,
        {
          staffId: selectedStaff._id,
          amount: parsed,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!data.success) throw new Error(data.message || "Failed to add coins");

      // Build transaction record for parent
      const tx = {
        id: data.data._id,
        date: new Date().toLocaleDateString("en-IN"),
        time: new Date().toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        name: selectedStaff.fullName,
        role: selectedStaff.rolePermission?.rolename || "—",
        phone: selectedStaff.phoneNumber,
        type: "credit",
        amount: parsed,
        note: "—",
        status: "completed",
        newBalance: data.data.balance,
      };

      onTransaction?.(tx);

      // Success alert
      await Swal.fire({
        title: "Coin Sent! 🎉",
        html: `
          <div style="font-size:14px;color:#374151;line-height:1.8">
            <strong style="color:#c51369">${parsed.toLocaleString(
              "en-IN"
            )} Coins </strong>
            have been credited to<br/>
            <strong>${selectedStaff.fullName}</strong>'s wallet.<br/>
            <span style="font-size:12px;color:#6b7280">
              New Balance: ₹${data.data.balance?.toLocaleString("en-IN") ?? "—"}
            </span>
          </div>
        `,
        icon: "success",
        confirmButtonText: "Done",
        confirmButtonColor: "#c51369",
        customClass: {
          popup: "!rounded-2xl",
          confirmButton: "!rounded-xl",
        },
        timer: 4000,
        timerProgressBar: true,
      });

      // Reset form
      handleClear();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Something went wrong.";

      // Check specifically for frozen wallet
      const isFrozen =
        err.response?.status === 400 && msg.toLowerCase().includes("frozen");

      await Swal.fire({
        title: isFrozen ? "Wallet Frozen ❄️" : "Transfer Failed",
        text: msg,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#c51369",
        customClass: {
          popup: "!rounded-2xl",
          confirmButton: "!rounded-xl",
        },
      });
    } finally {
      setSending(false);
    }
  };

  const handleClear = () => {
    setPhone("");
    setSelectedStaff(null);
    setAmount("");
    setFetchError("");
    setMobileStep(0);
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  const isValid = selectedStaff && parseFloat(amount) > 0;
  if (!isOpen) return null;

  // ── Staff list panel ───────────────────────────────────────────────────────
  const StaffPanel = (
    <div className="flex flex-col gap-3">
      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          Search by Phone Number
        </label>
        <div className="relative">
          <HiSearch
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={15}
          />
          <input
            type="tel"
            maxLength={10}
            placeholder="Enter phone number…"
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            className={inputWithIcon}
          />
          {loadingStaff && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-200 border-t-[#c51369]" />
            </span>
          )}
        </div>
      </div>

      {fetchError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-xs text-red-700">
          <HiX size={12} className="shrink-0" /> {fetchError}
        </div>
      )}

      {!fetchError && (
        <div
          className="flex flex-col gap-1.5 overflow-y-auto pr-0.5"
          style={{ maxHeight: "clamp(160px, 40vh, 224px)" }}
        >
          {!loadingStaff && staffList.length === 0 && (
            <p className="py-6 text-center text-xs text-gray-400">
              {phone
                ? "No staff found for this number."
                : "No staff available."}
            </p>
          )}
          {staffList.map((staff) => {
            const isSelected = selectedStaff?._id === staff._id;
            return (
              <button
                key={staff._id}
                onClick={() => handleSelectStaff(staff)}
                className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all active:scale-[0.98] ${
                  isSelected
                    ? "border-[#c51369]/30 bg-pink-50"
                    : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-gray-100"
                }`}
              >
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    isSelected
                      ? "bg-pink-100 text-[#c51369]"
                      : "border border-gray-200 bg-white text-gray-500"
                  }`}
                >
                  {getInitials(staff.fullName)}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`truncate text-sm font-semibold ${
                      isSelected ? "text-[#c51369]" : "text-gray-800"
                    }`}
                  >
                    {staff.fullName}
                  </p>
                  <p className="truncate text-[11px] text-gray-400">
                    {staff.rolePermission?.rolename || "Staff"} ·{" "}
                    {staff.phoneNumber}
                  </p>
                </div>
                {isSelected && (
                  <span className="ml-1 shrink-0 text-[#c51369]">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // ── Amount panel ───────────────────────────────────────────────────────────
  const AmountPanel = (
    <div className="flex flex-col gap-4">
      {selectedStaff ? (
        <div className="flex items-center gap-3 rounded-xl border border-[#c51369]/20 bg-pink-50 px-3 py-2.5 sm:px-4 sm:py-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-pink-100 bg-white text-xs font-bold text-[#c51369] sm:h-10 sm:w-10">
            {getInitials(selectedStaff.fullName)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-800">
              {selectedStaff.fullName}
            </p>
            <p className="truncate text-[11px] text-gray-400">
              {selectedStaff.rolePermission?.rolename || "Staff"} ·{" "}
              {selectedStaff.phoneNumber}
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedStaff(null);
              setMobileStep(0);
            }}
            className="ml-1 shrink-0 rounded p-0.5 text-gray-400 transition hover:text-red-400"
          >
            <HiX size={13} />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center rounded-xl border border-dashed border-gray-200 py-4 text-xs text-gray-400">
          Select a staff member from the list
        </div>
      )}

      <div>
        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
          Coins
        </label>
        <div className="relative">
          <GiCoins
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={14}
          />
          <input
            type="number"
            min="1"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputWithRupee}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[500, 1000, 2000, 5000].map((q) => (
          <button
            key={q}
            onClick={() => setAmount(String(q))}
            className={`flex items-center gap-1 rounded-lg border px-3 py-1 text-xs font-semibold transition-all active:scale-95 ${
              amount === String(q)
                ? "border-[#c51369] bg-pink-50 text-[#c51369]"
                : "border-gray-200 text-gray-500 hover:border-[#c51369] hover:text-[#c51369]"
            }`}
          >
            <GiCoins className="text-sm" />
            {q.toLocaleString("en-IN")}
          </button>
        ))}
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSend}
          disabled={!isValid || sending}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#c51369] py-2.5 text-sm font-semibold text-white transition hover:bg-[#a81058] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            <MdSend size={15} />
          )}
          {sending ? "Sending..." : "Send Coins"}
        </button>
        <button
          onClick={handleClear}
          className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 active:scale-[0.98]"
        >
          Clear
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* ── Header ── */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2 sm:gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-pink-50 sm:h-9 sm:w-9">
            <GiCoins className="text-[#c51369]" size={18} />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 sm:text-base">
              Send Coins
            </p>
            <p className="text-[11px] text-gray-400 sm:text-xs">
              Transfer conins to staff wallet
            </p>
          </div>
        </div>

        {/* Mobile step indicator dots */}
        <div className="flex items-center gap-3 sm:hidden">
          <div className="flex gap-1.5">
            <span
              className={`h-1.5 w-4 rounded-full transition-all ${
                mobileStep === 0 ? "bg-[#c51369]" : "bg-gray-200"
              }`}
            />
            <span
              className={`h-1.5 w-4 rounded-full transition-all ${
                mobileStep === 1 ? "bg-[#c51369]" : "bg-gray-200"
              }`}
            />
          </div>
        </div>
      </div>

      {/* ── Mobile: step-based layout ── */}
      <div className="block sm:hidden">
        {mobileStep === 0 ? (
          <div className="p-4">
            {StaffPanel}
            {selectedStaff && (
              <button
                onClick={() => setMobileStep(1)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#c51369] py-3 text-sm font-semibold text-white transition hover:bg-[#a81058] active:scale-[0.98]"
              >
                Continue to →
              </button>
            )}
          </div>
        ) : (
          <div className="p-4">
            <button
              onClick={() => setMobileStep(0)}
              className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-gray-400 transition hover:text-[#c51369]"
            >
              <span>←</span> Back to Staff List
            </button>
            {AmountPanel}
          </div>
        )}
      </div>

      {/* ── Tablet/Desktop: two-column layout ── */}
      <div className="hidden sm:grid sm:grid-cols-2 sm:divide-x sm:divide-gray-100">
        <div className="p-5">{StaffPanel}</div>
        <div className="p-5">{AmountPanel}</div>
      </div>
    </div>
  );
};

export default SendMoneyModal;
